import type { TranslatorRequest } from "@/features/ai-translator/translator-tool-utils"
import type { StateCreator } from "zustand"

export type ReasoningMode = "auto" | "fast" | "thinking" | "big_brain"
// Full reasoning-effort ladder across models. The model selector shows only the
// subset each model supports (from `supported_efforts` on GPTModelItem); the
// backend derives that per model in services/model_capabilities.py.
export type ReasoningEffortOverride =
  | "none"
  | "minimal"
  | "low"
  | "medium"
  | "high"
  | "xhigh"
  | "max"

export type ResponseTiming = {
  requestStartMs: number
  wsOpenMs: number | null
  firstActivityMs: number | null
  firstTokenMs: number | null
  endMs: number | null
  outputTokens: number | null
  reasoningTokens: number | null
}

export type ExplorerFeaturesSlice = {
  translationRequest: TranslatorRequest | null
  setTranslationRequest: (request: TranslatorRequest | null) => void
  filesInIndex: string[]
  setFilesInIndex: (files: string[]) => void
  enableAgent: boolean
  setEnableAgent: (enabled: boolean) => void
  enableDeep: boolean
  setEnableDeep: (enabled: boolean) => void
  /** Opt-in first-person agent narration during Deep Explorer runs. */
  enableNarration: boolean
  setEnableNarration: (enabled: boolean) => void
  /** Opt-in to the fact-checker verification pass (default off). When on,
   *  the executor dispatches the fact_checker subagent after the
   *  deliverable; when off, the pass is skipped entirely. */
  enableFactCheck: boolean
  setEnableFactCheck: (enabled: boolean) => void
  enableWebSearch: boolean
  setEnableWebSearch: (enabled: boolean) => void
  /** Per-session opt-in to hierarchical tree-walk retrieval (the
   *  `library_tree_search` agent tool). When on, the tool is included in
   *  the explorer's tool list and the LLM may escalate to it; when off,
   *  the tool is stripped from the request payload. Visibility of the
   *  footer toggle is additionally gated by the admin `tree_retrieval`
   *  feature flag. */
  enableTreeRetrieval: boolean
  setEnableTreeRetrieval: (enabled: boolean) => void
  reasoningMode: ReasoningMode
  setReasoningMode: (mode: ReasoningMode) => void
  modelOverride: string | null
  setModelOverride: (modelId: string | null) => void
  effortOverride: ReasoningEffortOverride | null
  setEffortOverride: (effort: ReasoningEffortOverride | null) => void
  hasInitializedDefaults: boolean
  setHasInitializedDefaults: (initialized: boolean) => void
  selectedPromptId: string | null
  selectedPromptTitle: string | null
  setSelectedPrompt: (id: string | null, title: string | null) => void
  clearSelectedPrompt: () => void
  enableM365: boolean
  setEnableM365: (enabled: boolean) => void
  enableServiceDesk: boolean
  setEnableServiceDesk: (enabled: boolean) => void
  enableNetbox: boolean
  setEnableNetbox: (enabled: boolean) => void
  enableXOne: boolean
  setEnableXOne: (enabled: boolean) => void
  /** Per-chat switch for searching the user's own mail, files and chats (default on, PS-5).
   *  The server holds the authoritative per-chat value; this mirrors it for the open chat. */
  personalSearchEnabled: boolean
  setPersonalSearchEnabled: (enabled: boolean) => void
  /** Per-chat master switch for the plugin-store tools (default on). */
  enablePlugins: boolean
  setEnablePlugins: (enabled: boolean) => void
  /** Installed plugin ids the user switched off for this chat. Kept as an
   *  exclusion list so a plugin installed mid-chat is on by default. */
  disabledPluginIds: string[]
  setDisabledPluginIds: (pluginIds: string[]) => void
  setPluginDisabled: (pluginId: string, disabled: boolean) => void
  /** The user's installed plugin ids as last loaded from the catalog; the
   *  chat request derives its `plugin_ids` allowlist from it. */
  installedPluginIds: string[]
  setInstalledPluginIds: (pluginIds: string[]) => void
  responseTiming: ResponseTiming | null
  startResponseTimer: () => void
  markWsOpen: () => void
  markFirstActivity: () => void
  markFirstToken: () => void
  markResponseEnd: (tokenInfo?: { outputTokens?: number; reasoningTokens?: number }) => void
  clearResponseTiming: () => void
}

export const createExplorerFeaturesSlice: StateCreator<ExplorerFeaturesSlice> = (set, get) => ({
  translationRequest: null,
  setTranslationRequest(request) {
    set({
      translationRequest: request,
      ...(request ? { enableAgent: false, enableDeep: false } : {}),
    })
  },
  filesInIndex: [],
  setFilesInIndex(files) {
    set({ filesInIndex: files })
  },
  enableAgent: false,
  setEnableAgent(enabled) {
    set({ enableAgent: enabled, ...(enabled ? { translationRequest: null } : {}) })
  },
  enableDeep: false,
  setEnableDeep(enabled) {
    set({ enableDeep: enabled, ...(enabled ? { translationRequest: null } : {}) })
  },
  enableNarration: false,
  setEnableNarration(enabled) {
    set({ enableNarration: enabled })
  },
  enableFactCheck: false,
  setEnableFactCheck(enabled) {
    set({ enableFactCheck: enabled })
  },
  enableWebSearch: false,
  setEnableWebSearch(enabled) {
    set({ enableWebSearch: enabled })
  },
  enableTreeRetrieval: false,
  setEnableTreeRetrieval(enabled) {
    set({ enableTreeRetrieval: enabled })
  },
  reasoningMode: "auto",
  setReasoningMode(mode) {
    set({ reasoningMode: mode })
  },
  modelOverride: null,
  setModelOverride(modelId) {
    set({ modelOverride: modelId })
  },
  effortOverride: null,
  setEffortOverride(effort) {
    set({ effortOverride: effort })
  },
  hasInitializedDefaults: false,
  setHasInitializedDefaults(initialized) {
    set({ hasInitializedDefaults: initialized })
  },
  selectedPromptId: null,
  selectedPromptTitle: null,
  setSelectedPrompt(id, title) {
    set({ selectedPromptId: id, selectedPromptTitle: title })
  },
  clearSelectedPrompt() {
    set({ selectedPromptId: null, selectedPromptTitle: null })
  },
  enableM365: false,
  setEnableM365(enabled) {
    set({ enableM365: enabled })
  },
  enableServiceDesk: false,
  setEnableServiceDesk(enabled) {
    set({ enableServiceDesk: enabled })
  },
  enableNetbox: false,
  setEnableNetbox(enabled) {
    set({ enableNetbox: enabled })
  },
  enableXOne: false,
  setEnableXOne(enabled) {
    set({ enableXOne: enabled })
  },
  personalSearchEnabled: true,
  setPersonalSearchEnabled(enabled) {
    set({ personalSearchEnabled: enabled })
  },
  enablePlugins: true,
  setEnablePlugins(enabled) {
    set({ enablePlugins: enabled })
  },
  disabledPluginIds: [],
  setDisabledPluginIds(pluginIds) {
    set({ disabledPluginIds: pluginIds })
  },
  setPluginDisabled(pluginId, disabled) {
    const current = get().disabledPluginIds
    if (disabled) {
      if (!current.includes(pluginId)) set({ disabledPluginIds: [...current, pluginId] })
    } else if (current.includes(pluginId)) {
      set({ disabledPluginIds: current.filter((id) => id !== pluginId) })
    }
  },
  installedPluginIds: [],
  setInstalledPluginIds(pluginIds) {
    const current = get().installedPluginIds
    if (
      current.length === pluginIds.length &&
      current.every((id, index) => id === pluginIds[index])
    )
      return
    set({ installedPluginIds: pluginIds })
  },
  responseTiming: null,
  startResponseTimer() {
    set({
      responseTiming: {
        requestStartMs: performance.now(),
        wsOpenMs: null,
        firstActivityMs: null,
        firstTokenMs: null,
        endMs: null,
        outputTokens: null,
        reasoningTokens: null,
      },
    })
  },
  markWsOpen() {
    const current = get().responseTiming
    if (current && current.wsOpenMs === null) {
      set({ responseTiming: { ...current, wsOpenMs: performance.now() } })
    }
  },
  markFirstActivity() {
    const current = get().responseTiming
    if (current && current.firstActivityMs === null) {
      set({ responseTiming: { ...current, firstActivityMs: performance.now() } })
    }
  },
  markFirstToken() {
    const current = get().responseTiming
    if (current && current.firstTokenMs === null) {
      set({ responseTiming: { ...current, firstTokenMs: performance.now() } })
    }
  },
  markResponseEnd(tokenInfo) {
    const current = get().responseTiming
    if (current && current.endMs === null) {
      set({
        responseTiming: {
          ...current,
          endMs: performance.now(),
          outputTokens: tokenInfo?.outputTokens ?? null,
          reasoningTokens: tokenInfo?.reasoningTokens ?? null,
        },
      })
    }
  },
  clearResponseTiming() {
    set({ responseTiming: null })
  },
})
